import {
  ChatPanel,
  MessageDestroyTip,
  MessageEncryptedTip,
  ChatPanelBodyPinned,
  MessageInviteCard,
  ChatPanelFooter,
  ChatPanelFooterError,
  MediaJoinCall,
  toaster,
  GroupJoinRequest,
  ConfirmDialog,
  tools,
  SessionErrorContent,
} from '@halo/ui-chat-react';
import { useShallowEnhance, useStore } from '@halo/zustand-enhance';
import type { MediaType } from '@halo/chat-sdk';
import { GroupStatusEnum, MsgPayloadType, GroupMemberRole } from '@halo/chat-sdk';
import { Fragment } from 'react/jsx-runtime';
import { Checkbox } from '@chakra-ui/react';
import { useNavigate } from 'react-router';
import { useEffect } from 'react';
import type { MediaStoreInstance } from '@/store/client';
import { useAppStore } from '@/store/client';
import { useActionsStore } from '@/store/actions';
import { DescriptionCardDrawer } from '@/components/DescriptionCardDrawer';
import { usePortalContainer, useUserInfo } from '@/scripts/hooks';
import Paths from '@/configs/router/paths';
import { ExitGroupDialog } from '@/components/ExitGroupDialog';
import { ForbidAddDialog } from '@/components/ForbidAddDialog';
import { ReportCardDrawer } from '@/components/ReportCardDrawer';
import ReportNotice from '@/components/ReportNotice';
import { ChatContentHeaderActions } from '../../ChatContent/ChatContentHeaderActions';
import { ChatContentDrawer } from '../../ChatContentDrawer';
import { SessionMessages } from '../../session-parts/session-messages';
import type { SessionFileDragRef } from '../../session-parts/session-file-drag';
import { SessionFileDrag } from '../../session-parts/session-file-drag';
import type { SessionProps } from '../types';
import {
  SessionBody,
  SessionComposer,
  SessionContentsContextProvider,
  SessionHeader,
  SessionMsgList,
} from '../../session-parts';

export interface SessionGroupContentProps extends SessionProps {
  mediaStoreInstance: MediaStoreInstance | undefined;
}

export function SessionGroupContent({
  visible,
  sessionStoreInstance,
  mediaStoreInstance,
}: SessionGroupContentProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    client,
    mediaJoin,
    updateSession,
    mediaActivatedId,
    hasOtherDeviceJoinCall,
    mediaStores,
    hideSession,
    packageErrorMap,
  } = useAppStore(
    useShallowEnhance([
      'client',
      'user',
      'mediaJoin',
      'updateSession',
      'activatedSessionId',
      'mediaActivatedId',
      'hasOtherDeviceJoinCall',
      'mediaStores',
      'hideSession',
      'packageErrorMap',
    ])
  );

  const {
    groupDescriptionIsOpen,
    setGroupInviteIsOpen,
    setGroupJoinRequestsIsOpen,
    setGroupDescriptionIsOpen,
    setGroupCreateCallIsOpen,
    setQrCodeIsOpen,
    exitGroupIsOpen,
    setExitGroupIsOpen,
    forbidAddIsOpen,
    setForbidAddIsOpen,
  } = useActionsStore(
    useShallowEnhance([
      'groupDescriptionIsOpen',
      'setGroupInviteIsOpen',
      'setLuckyDialogIsOpen',
      'setTransferToIsOpen',
      'setGroupJoinRequestsIsOpen',
      'setGroupDescriptionIsOpen',
      'setGroupCreateCallIsOpen',
      'setQrCodeIsOpen',
      'setSafetyToolsIsOpen',
      'exitGroupIsOpen',
      'setExitGroupIsOpen',
      'forbidAddIsOpen',
      'setForbidAddIsOpen',
    ])
  );
  const [openInfoDrawer, setOpenInfoDrawer] = useState(false);

  const sessionStore = useStore(sessionStoreInstance);

  const [showJoinRequest, setShowJoinRequest] = useState(true);

  const [clearChatHistory, setClearChatHistory] = useState(true);
  const [openLeave, setOpenLeave] = useState(false);
  const [leaveLoading, setLeaveLoading] = useState(false);
  const { portalContainerRef } = usePortalContainer();

  const canRestrictCopying = sessionStore?.groupAccess?.canRestrictCopying;
  const sessionFileDragRef = useRef<SessionFileDragRef>(null);

  const packageError = useMemo(() => {
    return packageErrorMap[sessionStore?.session?.chat_id || ''];
  }, [packageErrorMap, sessionStore?.session?.chat_id]);

  const isGroupInfoLoading = useMemo(() => {
    return !sessionStore?.groupInfo;
  }, [sessionStore?.groupInfo]);

  const isGroupDisband = useMemo(() => {
    return sessionStore?.groupInfo?.status === GroupStatusEnum.DISBAND;
  }, [sessionStore?.groupInfo?.status]);

  const isGroupBanned = useMemo(() => {
    return sessionStore?.groupInfo?.status === GroupStatusEnum.BAN;
  }, [sessionStore?.groupInfo?.status]);

  const isInGroup = useMemo(() => {
    return (
      sessionStore?.groupMyRole?.role === undefined ||
      sessionStore.groupMyRole.role !== GroupMemberRole.NONE
    );
  }, [sessionStore?.groupMyRole?.role]);

  const hasGroupJoins = useMemo(() => {
    return (sessionStore?.groupJoins?.length ?? 0) > 0;
  }, [sessionStore?.groupJoins?.length]);

  const isGroupManager = useMemo(() => {
    return sessionStore?.isGroupAdmin;
  }, [sessionStore?.isGroupAdmin]);

  const isGroupOwner = useMemo(() => {
    return sessionStore?.isGroupOwner;
  }, [sessionStore?.isGroupOwner]);

  const { sendMessageDisabled, mediaCallDisabled } = useMemo(() => {
    return {
      sendMessageDisabled: !sessionStore?.groupAccess?.canSendMessage,
      mediaCallDisabled: !sessionStore?.groupAccess?.canMediaCall,
    };
  }, [sessionStore?.groupAccess]);

  const disabledMediaJoin = useMemo(() => {
    return !!(mediaActivatedId && mediaActivatedId !== sessionStore?.session?.chat_id);
  }, [mediaActivatedId, sessionStore?.session?.chat_id]);

  const isCalling = useMemo(() => {
    return (
      !!mediaActivatedId ||
      Object.keys(mediaStores).includes(sessionStore?.session?.chat_id as any) ||
      hasOtherDeviceJoinCall
    );
  }, [hasOtherDeviceJoinCall, mediaActivatedId, mediaStores, sessionStore?.session?.chat_id]);

  const css = useMemo(() => {
    return {
      '--session-user-select': canRestrictCopying ? 'auto!important' : 'none!important',
    };
  }, [canRestrictCopying]);

  const onAvatarUpdate = useCallback(
    async (file: File) => {
      const { groupInfo, setGroupInfo } = sessionStore;
      const access_url = await client?.Api.session.uploadAvatar({ file });
      await client?.Api.session.setGroupInfo(
        sessionStore!.session!.chat_id,
        sessionStore!.session!.name!,
        access_url!
      );
      setGroupInfo({
        ...groupInfo!,
        avatar: access_url!,
      });
      updateSession({
        chat_id: sessionStore!.session!.chat_id!,
        avatar: access_url!,
      });
    },
    [client?.Api.session, sessionStore, updateSession]
  );

  const refSessionStore = useRef(sessionStore);
  refSessionStore.current = sessionStore;

  const handleAvatarClick = useCallback(async () => {
    if (isGroupDisband || !isInGroup) {
      return;
    }
    setOpenInfoDrawer(true);
  }, [isGroupDisband, isInGroup]);

  const handleGroupJoinRequest = useCallback(async () => {
    setGroupJoinRequestsIsOpen(true);
  }, [setGroupJoinRequestsIsOpen]);

  const { user: groupCreator } = useUserInfo(sessionStore?.groupInfo?.creator_uid);
  const { user: groupInviter } = useUserInfo(sessionStore?.groupMyRole?.invite_uid);

  const isGroupCreator = useMemo(() => {
    // group don't use sessionStore?.session?.is_creator, use groupInfo?.creator_uid instead
    return sessionStore?.groupInfo?.creator_uid === sessionStore?.user?.user_id;
  }, [sessionStore?.groupInfo?.creator_uid, sessionStore?.user?.user_id]);

  const isStrangerInvite = useMemo(() => {
    return sessionStore?.groupMyRole?.role === GroupMemberRole.STRANGER_INVITE;
  }, [sessionStore?.groupMyRole?.role]);

  const canSend = useMemo(() => {
    return !sendMessageDisabled && isInGroup && !isGroupDisband && !isGroupBanned;
  }, [sendMessageDisabled, isInGroup, isGroupDisband, isGroupBanned]);

  const hideMembers = useMemo(() => {
    return !sessionStore?.groupAccess?.canShowMembers;
  }, [sessionStore?.groupAccess?.canShowMembers]);

  const sendDisabledLabel = useMemo(() => {
    if (!isInGroup) {
      return t('chat.unableToSendMessageInExitedGroupChat');
    }
    if (isGroupDisband) {
      return t('chat.cannotMessageADisbandedGroup');
    }
    if (sendMessageDisabled) {
      return t('chat.onlySomeMembersCanSendInThisChat');
    }
    if (isGroupBanned) {
      return t('chat.groupBanned');
    }
    return '';
  }, [sendMessageDisabled, isInGroup, isGroupDisband, isGroupBanned, t]);

  const chatPanelBodyRef = useRef<HTMLDivElement>(null);

  const onStayGroupClick = useCallback(async () => {
    try {
      const result = await client?.Api.session.updateGroupMemberRole(
        sessionStore?.session?.chat_id ?? '',
        [sessionStore?.user?.user_id ?? ''],
        GroupMemberRole.MEMBER
      );
      if (result?.update_fail_list?.length) {
        tools.toaster.error({
          title: t('chat.failedToStayGroup'),
        });
      }
    } catch (error) {
      tools.toaster.error({
        title: (error as Error)?.message || t('chat.failedToStayGroup'),
      });
    }
  }, [sessionStore?.session?.chat_id, sessionStore?.user?.user_id, client?.Api.session, t]);

  const onLeaveGroup = useCallback(() => {
    if (leaveLoading) return;
    setLeaveLoading(true);
    client?.Api.session
      .quitGroup(sessionStore?.groupInfo?.gid ?? '')
      .then(async ({ quit_fail_list }) => {
        setOpenInfoDrawer(false);
        if (quit_fail_list.length > 0) {
          return tools.toaster.error({
            title: t('chat.failedToLeaveGroup'),
          });
        }
        if (clearChatHistory) {
          await hideSession(sessionStore?.groupInfo?.gid ?? '', clearChatHistory);
          navigate(Paths.MESSAGES_CHATS);
        }
        setOpenLeave(false);
      })
      .catch((error) => {
        tools.toaster.error({
          title: (error as Error)?.message || t('chat.failedToLeaveGroup'),
        });
      })
      .finally(() => {
        setLeaveLoading(false);
      });
  }, [
    leaveLoading,
    client?.Api.session,
    sessionStore?.groupInfo?.gid,
    setOpenInfoDrawer,
    clearChatHistory,
    hideSession,
    navigate,
    t,
  ]);

  const renderFirstMessage = useCallback(
    (key: string) => {
      return (
        <Fragment key={key}>
          <MessageEncryptedTip />
          <MessageDestroyTip />
          <MessageInviteCard
            isStrangerInvite={isStrangerInvite}
            isGroupAdmin={sessionStore?.isGroupAdmin}
            isGroupCreator={isGroupCreator}
            isNormalGroup={
              sessionStore?.groupInfo?.status === GroupStatusEnum.Normal &&
              sessionStore?.groupMyRole?.role !== GroupMemberRole.NONE
            }
            groupMyRole={sessionStore?.groupMyRole}
            groupInviter={groupInviter}
            groupCreator={groupCreator}
            session={sessionStore?.session}
            description={sessionStore?.groupInfo?.description || ''}
            setIsOpen={setGroupInviteIsOpen}
            onAvatarUpdate={onAvatarUpdate}
            onAddDescription={() => setGroupDescriptionIsOpen(true)}
            infoEditDisabled={!sessionStore?.groupAccess?.canEditGroupInfo}
            groupInfo={sessionStore?.groupInfo}
            onSettingClick={() => {
              setOpenInfoDrawer(true);
            }}
            onGroupLinkClick={() => {
              setQrCodeIsOpen(true, {
                link: sessionStore?.groupInfo?.share_url || '',
                avatar: sessionStore?.groupInfo?.avatar || '',
                name: sessionStore?.groupInfo?.group_name || '',
                username: sessionStore?.groupInfo?.group_id || '',
                source: 'group',
                sessionStore,
              });
            }}
            onStayClick={onStayGroupClick}
            onExitGroupClick={() => {
              setOpenLeave(true);
            }}
          />
          <ConfirmDialog
            disabledPortal={false}
            portalContainerRef={portalContainerRef}
            open={openLeave}
            onClose={() => setOpenLeave(false)}
            title={t('chat.leavingGroupChat', { name: sessionStore?.groupInfo?.group_name })}
            onOk={() => onLeaveGroup()}
            okText={t('buttons.leave')}
          >
            <Checkbox.Root
              size="sm"
              defaultChecked={clearChatHistory}
              onCheckedChange={(e) => setClearChatHistory(!!e.checked)}
              cursor="pointer"
            >
              <Checkbox.HiddenInput />
              <Checkbox.Control cursor="pointer" borderRadius="sm" />
              <Checkbox.Label>{t('chatSettings.clearChatHistory')}</Checkbox.Label>
            </Checkbox.Root>
          </ConfirmDialog>
        </Fragment>
      );
    },
    [
      isStrangerInvite,
      sessionStore,
      groupInviter,
      groupCreator,
      setGroupInviteIsOpen,
      onAvatarUpdate,
      onStayGroupClick,
      portalContainerRef,
      openLeave,
      t,
      clearChatHistory,
      setGroupDescriptionIsOpen,
      setQrCodeIsOpen,
      onLeaveGroup,
      isGroupCreator,
    ]
  );

  const showGroupJoinRequest_ = useMemo(() => {
    return hasGroupJoins && isGroupManager && showJoinRequest && !isGroupBanned;
  }, [hasGroupJoins, isGroupManager, showJoinRequest, isGroupBanned]);

  const handleMediaClick = useCallback(
    async (key: MediaType) => {
      switch (key) {
        case MsgPayloadType.MEDIA_AUDIO_CALL:
        case MsgPayloadType.MEDIA_VIDEO_CALL: {
          if (isGroupBanned) {
            return toaster.error({ title: t('chat.groupBanned') });
          }
          if (mediaCallDisabled) {
            return toaster.error({ title: t('calls.mediaCallDisabledTip') });
          }
          const members = sessionStore?.session?.chat_id
            ? await client?.Api.session.getGroupMembers({
                gid: sessionStore.session.chat_id,
              })
            : [];
          setGroupCreateCallIsOpen(true, {
            title: t('calls.selectMembers'),
            type: key,
            list: [
              {
                label: null,
                items: members || [],
              },
            ],
            session: sessionStore.session,
          });
          break;
        }
        default:
      }
    },
    [
      isGroupBanned,
      mediaCallDisabled,
      sessionStore.session,
      client?.Api.session,
      setGroupCreateCallIsOpen,
      t,
    ]
  );

  useEffect(() => {
    if (visible) return;
    setOpenInfoDrawer(false);
  }, [visible]);

  const reportNotice = useMemo(() => {
    const groupInfo = sessionStore?.groupInfo;
    if (!groupInfo) return { visible: false, type: undefined };

    if (isGroupBanned) {
      return { visible: true, type: 'group_banned' as const };
    }

    if (groupInfo.is_risk === 1) {
      return {
        visible: true,
        type: 'group_report_exceeded' as const,
      };
    }

    return { visible: false, type: undefined };
  }, [sessionStore?.groupInfo, isGroupBanned]);

  return (
    <SessionContentsContextProvider
      sessionStoreInstance={sessionStoreInstance}
      canMention={canSend}
      hideMembers={hideMembers}
    >
      <ChatPanel visible={visible} css={css}>
        <SessionHeader
          sessionStore={sessionStore}
          isGroup={true}
          isGroupDisband={isGroupDisband}
          isInGroup={isInGroup}
          onAvatarClick={handleAvatarClick}
          extra={
            <ChatContentHeaderActions
              sessionStore={sessionStore}
              isGroupError={isGroupDisband || !isInGroup}
              isGroup={true}
              isMe={false}
              isCalling={isCalling}
              onMediaClick={handleMediaClick}
              onSettingClick={() => {
                setOpenInfoDrawer(true);
              }}
            />
          }
        />
        <SessionBody ref={chatPanelBodyRef}>
          {packageError && <SessionErrorContent packageError={packageError} />}
          <SessionFileDrag
            ref={sessionFileDragRef}
            bodyRef={chatPanelBodyRef}
            sessionStoreInstance={sessionStoreInstance}
            sessionName={sessionStore?.session?.name || ''}
            disabled={!canSend}
            disabledLabel={sendDisabledLabel}
          />
          {mediaStoreInstance && (
            <ChatPanelBodyPinned>
              <MediaJoinCall
                disabled={disabledMediaJoin}
                mediaStoreInstance={mediaStoreInstance}
                mediaJoin={async () => {
                  await mediaJoin({
                    session_id: sessionStore.session!.chat_id,
                  }).catch(() => {
                    toaster.error({ title: t('calls.joinFailed') });
                  });
                }}
              />
            </ChatPanelBodyPinned>
          )}
          <ChatPanelBodyPinned>
            <ReportNotice type={reportNotice.type} visible={reportNotice.visible} />
            {showGroupJoinRequest_ && (
              <GroupJoinRequest
                joinRequestCount={sessionStore?.groupJoins?.length ?? 0}
                onHandle={handleGroupJoinRequest}
                onClose={() => setShowJoinRequest(false)}
              />
            )}
          </ChatPanelBodyPinned>
          <SessionMsgList sessionStore={sessionStore}>
            <SessionMessages
              renderFirstMessage={renderFirstMessage}
              sessionStore={sessionStore}
              sendMessageDisabled={sendMessageDisabled}
            />
          </SessionMsgList>
          <ChatPanelFooter>
            {isGroupBanned ? (
              <ChatPanelFooterError>{t('chat.groupBanned')}</ChatPanelFooterError>
            ) : isGroupDisband ? (
              <ChatPanelFooterError>
                {t('chat.cannotMessageADisbandedGroup')}
              </ChatPanelFooterError>
            ) : !isInGroup ? (
              <ChatPanelFooterError>
                {t('chat.unableToSendMessageInExitedGroupChat')}
              </ChatPanelFooterError>
            ) : (
              <SessionComposer
                loading={isGroupInfoLoading}
                isGroup={true}
                visible={visible}
                sessionStore={sessionStore}
                disabled={sendMessageDisabled}
                onPickFile={sessionFileDragRef.current?.handleSendFile}
                onSend={() => {
                  if (isStrangerInvite) {
                    onStayGroupClick();
                  }
                }}
              ></SessionComposer>
            )}
          </ChatPanelFooter>
          <ChatContentDrawer
            open={openInfoDrawer}
            setOpenInfoDrawer={setOpenInfoDrawer}
            sessionStore={sessionStore}
            isGroupManager={isGroupManager}
            isGroupOwner={isGroupOwner}
            isGroupBanned={isGroupBanned}
          />
          <DescriptionCardDrawer
            sessionStore={sessionStore}
            description={sessionStore?.groupInfo?.description || ''}
            isOpen={groupDescriptionIsOpen}
            setIsOpen={setGroupDescriptionIsOpen}
            closeAllClick={() => setOpenInfoDrawer(false)}
          />
          <ReportCardDrawer
            closeAllClick={() => setOpenInfoDrawer(false)}
            from="group"
            id={sessionStore?.groupInfo?.gid || ''}
          />
          <ExitGroupDialog
            isOpen={exitGroupIsOpen}
            setIsOpen={setExitGroupIsOpen}
            avatar={sessionStore?.groupInfo?.avatar || ''}
            name={sessionStore?.groupInfo?.group_name || ''}
          />
          <ForbidAddDialog
            isOpen={forbidAddIsOpen}
            setIsOpen={setForbidAddIsOpen}
            endTime={sessionStore?.groupInfo?.risk_end_time ?? 0}
          />
        </SessionBody>
      </ChatPanel>
    </SessionContentsContextProvider>
  );
}
